import { useState, useEffect } from 'preact/hooks'
import { X, ArrowLeft, ArrowRight, User, Users } from 'lucide-preact'
import { useAuth } from '../../auth/useAuth'
import { userRolesApi } from '../../api/userRoles'
import { RoleSelector } from '../common/RoleSelector'
import { useToast } from '../../hooks/useToast'
import type {
  UserProfileCreateDto,
  UserRoleAssignmentDto,
  UserProfileWithRolesDto,
} from '../../types/userRoles'
import type { ClientCompanyViewDto, ClientWorkspaceViewDto } from '../../types/company'
import { isValidEmail } from '../../utils/validation'

interface UserCreateModalProps {
  isOpen: boolean
  selectedCompany: ClientCompanyViewDto
  onClose: () => void
  onSuccess: (newUser: UserProfileWithRolesDto) => void
}

type Step = 'details' | 'roles'

interface UserDetails {
  firstName: string
  lastName: string
  email: string
}

export function UserCreateModal({
  isOpen,
  selectedCompany,
  onClose,
  onSuccess
}: UserCreateModalProps) {
  const { userProfile, allWorkspaces } = useAuth()
  const { showSuccess } = useToast()
  const [currentStep, setCurrentStep] = useState<Step>('details')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [collapsedWorkspaces, setCollapsedWorkspaces] = useState<Set<string>>(new Set())

  // Get workspaces from auth state instead of API
  const [companyWorkspaces, setCompanyWorkspaces] = useState<ClientWorkspaceViewDto[]>([])
  const [fetchedCompanyData, setFetchedCompanyData] = useState<ClientCompanyViewDto | null>(null)

  // Step 1: User details
  const [userDetails, setUserDetails] = useState<UserDetails>({
    firstName: '',
    lastName: '',
    email: ''
  })
  const [detailsErrors, setDetailsErrors] = useState<Record<string, string>>({})

  // Step 2: Role assignment
  const [companyRoles, setCompanyRoles] = useState<string[]>([])
  const [workspaceRoles, setWorkspaceRoles] = useState<Record<string, string[]>>({})
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([])

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('details')
      setUserDetails({ firstName: '', lastName: '', email: '' })
      setDetailsErrors({})
      setCompanyRoles([])
      setWorkspaceRoles({})
      setSelectedWorkspaces([])
      setError(null)
      setCollapsedWorkspaces(new Set())
      setFetchedCompanyData(null)
      // Load workspaces from auth state
      const workspaces = (allWorkspaces || []).filter(w => w.ClientCompanyId === selectedCompany.Id)
      setCompanyWorkspaces(workspaces as ClientWorkspaceViewDto[])
      setFetchedCompanyData(selectedCompany)
    }
  }, [isOpen, selectedCompany.Id, allWorkspaces])

  // Validate user details (Step 1)
  const validateUserDetails = (): boolean => {
    const errors: Record<string, string> = {}

    if (!userDetails.firstName.trim()) {
      errors.firstName = 'First name is required'
    }

    if (!userDetails.lastName.trim()) {
      errors.lastName = 'Last name is required'
    }

    if (!userDetails.email.trim()) {
      errors.email = 'Email is required'
    } else if (!isValidEmail(userDetails.email)) {
      errors.email = 'Please enter a valid email address'
    }

    setDetailsErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Validate role assignments (Step 2)
  const validateRoleAssignments = (): boolean => {
    const hasCompanyRoles = companyRoles.length > 0
    const hasWorkspaceRoles = selectedWorkspaces.some(wsId =>
      (workspaceRoles[wsId] || []).length > 0
    )

    // User must have at least one role at either company OR workspace level
    // Company roles can exist without workspace roles (and vice versa)
    if (!hasCompanyRoles && !hasWorkspaceRoles) {
      setError('User must have at least one role assigned at either company or workspace level')
      return false
    }

    setError(null)
    return true
  }

  const handleDetailsNext = () => {
    if (validateUserDetails()) {
      setCurrentStep('roles')
    }
  }

  const handleRolesBack = () => {
    setCurrentStep('details')
    setError(null)
  }

  const handleCompanyRoleChange = (role: string, checked: boolean) => {
    // Single selection: set to just this role, or clear if deselecting
    setCompanyRoles(checked ? [role] : [])
    setError(null)
  }

  const handleWorkspaceRoleChange = (workspaceId: string, role: string, checked: boolean) => {
    setWorkspaceRoles(prev => {
      const newRoles = {
        ...prev,
        // Single selection per workspace: set to just this role, or clear if deselecting
        [workspaceId]: checked ? [role] : []
      }

      // Auto-update selectedWorkspaces based on which workspaces have roles
      const workspacesWithRoles = Object.keys(newRoles).filter(wsId =>
        (newRoles[wsId] || []).length > 0
      )
      setSelectedWorkspaces(workspacesWithRoles)

      return newRoles
    })
    setError(null)
  }

  const toggleWorkspaceCollapse = (workspaceId: string) => {
    setCollapsedWorkspaces(prev => {
      const newSet = new Set(prev)
      if (newSet.has(workspaceId)) {
        newSet.delete(workspaceId)
      } else {
        newSet.add(workspaceId)
      }
      return newSet
    })
  }

  const handleCreateUser = async () => {
    if (!validateRoleAssignments()) {
      return
    }

    // Use fetchedCompanyData if available, otherwise fall back to selectedCompany
    const companyToUse = fetchedCompanyData || selectedCompany
    const authOrganizationId = companyToUse.AuthOrganizationId

    if (!userProfile?.ClientCompanyId || !authOrganizationId) {
      setError('Missing required company information. Please ensure the company has an Auth0 organization configured.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Build workspace roles array only for workspaces that have roles assigned
      const workspacesWithRoles = Object.keys(workspaceRoles).filter(wsId =>
        (workspaceRoles[wsId] || []).length > 0
      )

      const workspaceRolesList = workspacesWithRoles.map(workspaceId => ({
        WorkspaceId: workspaceId,
        Roles: workspaceRoles[workspaceId] || []
      }))

      const roleAssignment: UserRoleAssignmentDto = {
        CompanyRoles: companyRoles,
        WorkspaceRoles: workspaceRolesList
      }

      const createDto: UserProfileCreateDto = {
        Auth0OrganizationId: authOrganizationId,
        ClientCompanyId: companyToUse.Id,
        FirstName: userDetails.firstName.trim(),
        LastName: userDetails.lastName.trim(),
        Email: userDetails.email.trim(),
        InitialRoles: roleAssignment
      }

      const response = await userRolesApi.createUser(createDto)

      if (response.error) {
        // For 422 validation errors, toasts are shown automatically by global handler
        // Just return to let user see the toast notifications
        if (response.status === 422) {
          return
        }
        // For other errors, show in modal
        setError(response.error)
        return
      }

      if (response.data) {
        const { Status, UserProfileWithRoles } = response.data
        
        // Show contextual toast message based on creation status
        switch (Status) {
          case 'Created':
            showSuccess('User Invited', 'User invited successfully')
            break
          case 'ExistingUserAdded':
            showSuccess('User Added', 'User from managing company added to this company')
            break
          case 'Reactivated':
            showSuccess('User Reactivated', 'User reactivated and invited')
            break
        }
        
        onSuccess(UserProfileWithRoles)
        onClose()
      } else {
        setError('User created but no data returned')
      }
    } catch (err) {
      setError('Failed to create user. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-over panel */}
      <div className="fixed inset-y-0 right-0 max-w-full flex">
        <div className="w-screen max-w-4xl">
          <div className="h-full flex flex-col bg-white dark:bg-slate-900 shadow-xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-lg font-medium text-slate-900 dark:text-white">
                      Add New User
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {currentStep === 'details' ? 'Step 1: User Information' : 'Step 2: Role Assignment'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-slate-400 hover:text-slate-500 dark:hover:text-slate-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Progress indicator */}
              <div className="mt-4">
                <div className="flex items-center">
                  <div className={`flex items-center ${currentStep === 'details' ? 'text-primary-600 dark:text-primary-400' : 'text-slate-400'}`}>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-medium ${currentStep === 'details'
                        ? 'border-primary-600 dark:border-primary-400 bg-primary-600 dark:bg-primary-400 text-white'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-400'
                      }`}>
                      1
                    </div>
                    <span className="ml-2 text-sm font-medium">Details</span>
                  </div>
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700 mx-4" />
                  <div className={`flex items-center ${currentStep === 'roles' ? 'text-primary-600 dark:text-primary-400' : 'text-slate-400'}`}>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-medium ${currentStep === 'roles'
                        ? 'border-primary-600 dark:border-primary-400 bg-primary-600 dark:bg-primary-400 text-white'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-400'
                      }`}>
                      2
                    </div>
                    <span className="ml-2 text-sm font-medium">Roles</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 py-6">
                {currentStep === 'details' ? (
                  /* Step 1: User Details */
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-4">
                        User Information
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                        Enter the basic information for the new user. An invitation email will be sent to the provided email address.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          First Name *
                        </label>
                        <input
                          type="text"
                          value={userDetails.firstName}
                          onChange={(e) => setUserDetails(prev => ({
                            ...prev,
                            firstName: (e.target as HTMLInputElement).value
                          }))}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${detailsErrors.firstName
                              ? 'border-red-300 dark:border-red-600'
                              : 'border-slate-300 dark:border-slate-600'
                            } bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100`}
                          placeholder="Enter first name"
                        />
                        {detailsErrors.firstName && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                            {detailsErrors.firstName}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          value={userDetails.lastName}
                          onChange={(e) => setUserDetails(prev => ({
                            ...prev,
                            lastName: (e.target as HTMLInputElement).value
                          }))}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${detailsErrors.lastName
                              ? 'border-red-300 dark:border-red-600'
                              : 'border-slate-300 dark:border-slate-600'
                            } bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100`}
                          placeholder="Enter last name"
                        />
                        {detailsErrors.lastName && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                            {detailsErrors.lastName}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={userDetails.email}
                        onChange={(e) => setUserDetails(prev => ({
                          ...prev,
                          email: (e.target as HTMLInputElement).value
                        }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${detailsErrors.email
                            ? 'border-red-300 dark:border-red-600'
                            : 'border-slate-300 dark:border-slate-600'
                          } bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100`}
                        placeholder="Enter email address"
                      />
                      {detailsErrors.email && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                          {detailsErrors.email}
                        </p>
                      )}
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        An invitation email will be sent to this address
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Step 2: Role Assignment */
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                        Role Assignment
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                        Assign roles to <span className="font-medium">{userDetails.firstName} {userDetails.lastName}</span>.
                        At least one role must be assigned at either company or workspace level.
                      </p>
                    </div>

                    <RoleSelector
                      companyRoles={companyRoles}
                      workspaceRoles={workspaceRoles}
                      selectedWorkspaces={selectedWorkspaces}
                      companyWorkspaces={companyWorkspaces}
                      onCompanyRoleChange={handleCompanyRoleChange}
                      onWorkspaceRoleChange={handleWorkspaceRoleChange}
                      collapsedWorkspaces={collapsedWorkspaces}
                      onToggleWorkspaceCollapse={toggleWorkspaceCollapse}
                      showEffectiveRoles={true}
                    />

                    {error && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  {currentStep === 'roles' && (
                    <button
                      onClick={handleRolesBack}
                      disabled={loading}
                      className="flex items-center space-x-2 px-4 py-2 text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Back</span>
                    </button>
                  )}
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={onClose}
                    disabled={loading}
                    className="px-4 py-2 text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>

                  {currentStep === 'details' ? (
                    <button
                      onClick={handleDetailsNext}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                    >
                      <span>Next</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={handleCreateUser}
                      disabled={loading}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      <Users className="w-4 h-4" />
                      <span>{loading ? 'Creating...' : 'Create User'}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
